/**
 * ⟦C62 · lot événements · A-179⟧ **Ce qu'un choix d'événement FAIT, dit en chiffres et en icônes.**
 *
 * Matt, 21/10/2026 : *« Il faut que le jeu soit simple et limpide. »* L'audit « regard neuf » a trouvé,
 * sur la fenêtre d'événement, un choix sans la moindre conséquence écrite : le joueur tranche à
 * l'aveugle, puis le Journal ne répète que le libellé choisi. Chaque bouton montre donc, sous son
 * libellé, ce que le choix va déplacer : `−3 ❤`, `+20 👥`, `−1 idée`.
 *
 * ## Ce que ce module change à ⟦SPEC-UI §3.6⟧, et ce qu'il laisse
 *
 * ⟦SPEC-UI §3.6⟧ (et ⟦A-49⟧) voulaient que l'écran ne lise JAMAIS `effets` : *« le contenu décide de ce
 * qui est télégraphié »*, par une phrase (`hint`) qu'aucun choix n'a jamais portée. C'est ⟦A-179⟧ qui
 * lève ce verrou, pour la fenêtre d'événement seule, et c'est le réglage de contenu
 * `meta.evenements_ecran.consequences_visibles` qui l'allume (absent ⇒ l'écran d'avant).
 *
 * Deux garde-fous tiennent la raison d'être du verrou, qui était : *un chiffre recopié des `effets`
 * dériverait au premier rééquilibrage.*
 *
 *  1. **Aucun chiffre n'est recopié, aucune règle n'est réécrite.** Les écarts viennent du MOTEUR :
 *     `applyEffects` (`@maxime/shared`) rejoue les effets du choix sur une COPIE de l'état, avec les
 *     interrupteurs du ménage, et on lit la différence. Le plafond de −15 % d'abonnés, les traits de
 *     genre, les bornes des jauges, l'argent ignoré avant le Grand Saut, les strikes coupés : tout y est,
 *     parce que c'est le code qui s'applique pour de vrai au clic.
 *  2. **L'écran ne montre que ce que le joueur connaît.** Un écart de réputation ne s'écrit pas quand le
 *     ménage a retiré la réputation de l'écran ; la Hype attend que le Fil soit découvert ; les Clicks
 *     n'existent qu'après le Grand Saut (le moteur les ignore d'ici là, donc aucun écart à montrer).
 *
 * ## Ce qui reste caché, exprès
 *
 * Les drapeaux, les relations avec les rivaux, les événements programmés, l'image de chaîne, le poids des
 * tirages : ce sont les fils de l'histoire, pas des conséquences chiffrées. Ils ne s'écrivent pas.
 *
 * Pur : aucun DOM, aucun store. Dépend du contenu (`interrupteurs()`) et de l'escalier, comme `hud.ts`.
 */

import { H_JOUR, applyEffects, type Effect, type GameState, type Systemes } from "@maxime/shared";

import { ouvert } from "./escalier";
import { nombre } from "./format";
import { interrupteurs } from "./menage";
import { JAUGE_RECAP } from "./recap";

/** Les jauges dont l'écart se lit dans la différence des états (leur nom et leur pictogramme : `JAUGE_RECAP`). */
type CleJauge = "abonnes" | "energie" | "sante_mentale" | "fidelite" | "reputation" | "argent" | "momentum" | "hype";

export type CleConsequence =
  | CleJauge
  | "idees"
  | "points"
  | "vues"
  | "qualite"
  | "accroche"
  | "xp"
  | "revenus"
  | "vieilles_videos"
  | "desabonnements";

export interface Consequence {
  readonly cle: CleConsequence;
  /** Le pictogramme, ou `""` quand le mot suffit (« −1 idée »). */
  readonly icone: string;
  /** Ce qui s'écrit sur le bouton : `−3 ❤`, `+20 👥`, `−1 idée`, `+5 🤝 fidélité`, `−20 % vues · 10 j`. */
  readonly texte: string;
  /** La phrase pour l'infobulle et le lecteur d'écran : `Santé mentale : −3`. */
  readonly phrase: string;
  /** Bon ou mauvais pour le joueur : la couleur du chiffre. */
  readonly ton: "gain" | "perte";
}

/** Le signe typographique : un vrai moins (U+2212), comme partout ailleurs dans l'interface. */
function signe(v: number): string {
  return v > 0 ? "+" : "−";
}

/** Une ligne de jauge. `avecNom` : le mot s'écrit à côté de l'icône (les jauges que le tableau de bord ne nomme pas). */
function ligneDeJauge(
  cle: CleJauge,
  delta: number,
  avecNom: boolean,
  decimales = 0,
): Consequence {
  const { icone, nom } = JAUGE_RECAP[cle];
  const absolu = Math.abs(delta);
  const valeur = decimales > 0 ? absolu.toFixed(decimales).replace(".", ",") : nombre(absolu);
  const brut = `${signe(delta)}${valeur}`;
  return {
    cle,
    icone,
    texte: avecNom ? `${brut} ${icone} ${nom.toLowerCase()}` : `${brut} ${icone}`,
    phrase: `${nom} : ${brut}`,
    ton: delta > 0 ? "gain" : "perte",
  };
}

/** Une ligne d'idées : le MOT, jamais le pictogramme de la carte (« un seul mot à l'écran : idée »). */
function ligneDIdees(delta: number): Consequence {
  const n = Math.abs(delta);
  const brut = `${signe(delta)}${nombre(n)}`;
  const mot = n > 1 ? "idées" : "idée";
  return { cle: "idees", icone: "", texte: `${brut} ${mot}`, phrase: `${brut} ${mot} en main`, ton: delta > 0 ? "gain" : "perte" };
}

/** Les cibles de `multiplier` que le joueur connaît, avec le mot qui les dit. */
const CIBLES_TEMPORAIRES: Readonly<
  Record<string, { cle: CleConsequence; nom: string; argent?: boolean }>
> = {
  vues: { cle: "vues", nom: "vues" },
  // La diffusion est la portée d'une vidéo : le joueur la vit comme des vues en plus ou en moins.
  diffusion: { cle: "vues", nom: "vues" },
  qualite: { cle: "qualite", nom: "qualité" },
  accroche: { cle: "accroche", nom: "accroche" },
  xp: { cle: "xp", nom: "XP" },
  rente: { cle: "vieilles_videos", nom: "vieilles vidéos" },
  rpm: { cle: "revenus", nom: "revenus", argent: true },
  revenus: { cle: "revenus", nom: "revenus", argent: true },
};

/** « 112 h » de durée simulée, dites en jours du joueur (une journée fait `H_JOUR` heures), 1 j au moins. */
function enJours(dureeH: number): number {
  return Math.max(1, Math.round(dureeH / H_JOUR));
}

/**
 * Les effets de durée (un multiplicateur de vues pendant dix jours, les désabonnements ×0,95) : une ligne par
 * mot, les valeurs de même mot ET de même durée se composent (`vues ×1,1` et `vues ×0,8` : `vues ×0,88`).
 */
function lignesTemporaires(effets: readonly Effect[], argentOuvert: boolean): Consequence[] {
  const groupes = new Map<string, { cle: CleConsequence; nom: string; produit: number; jours: number | null; inverse: boolean }>();
  for (const e of effets) {
    if (e.type === "multiplier") {
      const cible = CIBLES_TEMPORAIRES[e.target];
      if (!cible || (cible.argent && !argentOuvert)) continue;
      const jours = e.duree_h === undefined ? null : enJours(e.duree_h);
      const cle = `${cible.nom}|${jours ?? "permanent"}`;
      const g = groupes.get(cle);
      if (g) g.produit *= e.value;
      else groupes.set(cle, { cle: cible.cle, nom: cible.nom, produit: e.value, jours, inverse: false });
    } else if (e.type === "unsub_mult") {
      const cle = "désabonnements|permanent";
      const g = groupes.get(cle);
      if (g) g.produit *= e.value;
      else groupes.set(cle, { cle: "desabonnements", nom: "désabonnements", produit: e.value, jours: null, inverse: true });
    }
  }
  const lignes: Consequence[] = [];
  for (const g of groupes.values()) {
    const pct = Math.round((g.produit - 1) * 100);
    if (pct === 0) continue;
    const duree = g.jours === null ? "" : ` · ${g.jours} j`;
    const brut = `${signe(pct)}${nombre(Math.abs(pct))} % ${g.nom}`;
    // Moins de désabonnements, c'est bon : le sens du chiffre n'est pas celui de la couleur.
    const bon = g.inverse ? pct < 0 : pct > 0;
    lignes.push({ cle: g.cle, icone: "", texte: `${brut}${duree}`, phrase: `${brut}${g.jours === null ? "" : ` pendant ${g.jours} jour${g.jours > 1 ? "s" : ""}`}`, ton: bon ? "gain" : "perte" });
  }
  return lignes;
}

/**
 * Ce que ces effets vont faire à cet état, dans l'ordre où le joueur les lit : abonnés, énergie, santé
 * mentale, idées, les jauges plus discrètes (fidélité, réputation, Clicks, momentum, Hype), puis les effets
 * qui durent. Rien quand le choix ne déplace rien de visible (un drapeau, une relation, un événement
 * programmé) : le bouton n'écrit alors que son libellé.
 */
export function consequencesDesEffets(
  state: GameState,
  effets: readonly Effect[],
  sourceId?: string,
  /** Les interrupteurs du ménage ; par défaut ceux du contenu chargé (les tests rejouent le jeu d'avant). */
  sys: Systemes = interrupteurs(),
): readonly Consequence[] {
  const apres = applyEffects(state, effets, "event", { ...(sourceId ? { source_id: sourceId } : {}), systemes: sys });
  const s = apres.state;
  const lignes: Consequence[] = [];

  const abonnes = apres.subs_delta;
  if (abonnes !== 0) lignes.push(ligneDeJauge("abonnes", abonnes, false));

  const energie = Math.round(s.energy - state.energy);
  if (energie !== 0) lignes.push(ligneDeJauge("energie", energie, false));

  const mental = Math.round(s.mental - state.mental);
  if (mental !== 0) lignes.push(ligneDeJauge("sante_mentale", mental, false));

  // Les idées tirées (`cards_to_draw`, le tirage a lieu côté appelant) moins celles défaussées (la main rétrécit).
  const idees = apres.cards_to_draw - (state.inspiration.length - s.inspiration.length);
  if (idees !== 0) lignes.push(ligneDIdees(idees));

  const fidelite = Math.round(s.loyalty - state.loyalty);
  if (fidelite !== 0) lignes.push(ligneDeJauge("fidelite", fidelite, true));

  // ⟦C60 bis⟧ La réputation est retirée de l'écran : un écart qu'on ne peut pas lire ne s'écrit pas.
  const reputation = Math.round(s.reputation - state.reputation);
  if (reputation !== 0 && sys.reputation) lignes.push(ligneDeJauge("reputation", reputation, true));

  // Le moteur ignore déjà l'argent avant le Grand Saut : sans l'étage, il n'y a pas d'écart à montrer.
  const argentOuvert = ouvert(state, "argent");
  const argent = Math.round(s.money - state.money);
  if (argent !== 0 && argentOuvert) lignes.push(ligneDeJauge("argent", argent, false));

  const momentum = Math.round((s.momentum - state.momentum) * 100) / 100;
  if (momentum !== 0) lignes.push(ligneDeJauge("momentum", momentum, true, 2));

  // La Hype vit avec le Fil : tant qu'il n'est pas découvert, le joueur n'a jamais vu cette jauge.
  const hype = Math.round((s.hype ?? 0) - (state.hype ?? 0));
  if (hype !== 0 && state.flags.fil_decouvert === true) lignes.push(ligneDeJauge("hype", hype, true));

  if (apres.pc_gained > 0) {
    const brut = `+${nombre(apres.pc_gained)}`;
    lignes.push({
      cle: "points",
      icone: "",
      texte: `${brut} point${apres.pc_gained > 1 ? "s" : ""}`,
      phrase: `${brut} point${apres.pc_gained > 1 ? "s" : ""} à placer`,
      ton: "gain",
    });
  }

  lignes.push(...lignesTemporaires(effets, argentOuvert));
  return lignes;
}
